/**
 * Client for the AskIAM Assistant Guidance skill (`POST /api/assistant/chat`).
 *
 * The endpoint is feature-gated (`Assistant.Guidance`) and returns 404 when the flag is off, so
 * every caller lives behind `useFeature('Assistant.Guidance')`. Retrieval is grounded on the
 * current `message` only — `history` is advisory context for phrasing, capped server-side at 20
 * turns — so we trim it here to stay inside that bound.
 */
import { postJson } from './client';

/** "user" or "assistant" — one prior turn supplied back to the composer for continuity. */
export interface AssistantTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** A server-authoritative path the assistant surfaced (the LLM never originates these). */
export interface AssistantSource {
  /** tool | process | support-pattern */
  kind: string;
  title: string;
  description: string;
  /** askiam-request-type | url | ghd | icm | instructions */
  targetType: string;
  /** Request-type id, absolute URL, or markdown instructions (by targetType). */
  targetValue: string;
  /** Blended 0-100 display match strength (never a routing decision). */
  matchStrength: number;
}

export interface AssistantChatRequest {
  message: string;
  history: AssistantTurn[];
  conversationId?: string;
}

/** What the assistant decided to do with a turn. */
export type AssistantIntent = 'guidance' | 'support' | 'degraded' | 'noPath' | 'capability';

export interface AssistantChatResponse {
  reviewId: string;
  reply: string;
  intent: AssistantIntent | string;
  sources: AssistantSource[];
  /** "ghd" | "retry" | null */
  fallback?: string | null;
  helpdeskUrl?: string | null;
  degraded: boolean;
}

/** Server caps history at 20 turns; keep the most recent so a long chat still sends. */
export const MAX_ASSISTANT_HISTORY_TURNS = 20;

export function sendAssistantChat(request: AssistantChatRequest): Promise<AssistantChatResponse> {
  const history = request.history.slice(-MAX_ASSISTANT_HISTORY_TURNS);
  return postJson<AssistantChatResponse>('/api/assistant/chat', { ...request, history });
}

/** A thumbs up/down vote on one assistant answer. */
export interface AssistantFeedbackRequest {
  /** The `reviewId` from the answered turn's `AssistantChatResponse`. */
  reviewId: string;
  /** true = 👍 (reinforce), false = 👎 (negative signal). */
  helpful: boolean;
  /** The surfaced path being judged (top source's `targetValue`); omit for a no-source answer. */
  targetValue?: string;
}

/**
 * Records a thumbs up/down on an assistant answer. Best-effort and fire-and-forget from the UI's
 * perspective: the endpoint always acknowledges, so a vote never blocks or errors the conversation.
 */
export function sendAssistantFeedback(request: AssistantFeedbackRequest): Promise<{ recorded: boolean }> {
  return postJson<{ recorded: boolean }>('/api/assistant/feedback', request);
}

// ── Guided request Collector (`POST /api/assistant/collect/turn`) ──
// Gated by `Assistant.Collect` (404 when off). The server is authoritative: it re-sanitizes the
// echoed `collected` map against the template every turn, so the client only ever holds an advisory
// draft. Submission is NOT here — a "review" outcome is confirmed via `createRequest` (POST /api/requests).

/** A choice offered for a select field being collected. */
export interface AssistantCollectOption {
  value: string;
  label: string;
}

/** The field the collector is currently asking for. */
export interface AssistantCollectField {
  key: string;
  label: string;
  /** text | textarea | number | email | url | date | select | checkbox | repeatableText */
  type: string;
  helperText?: string | null;
  placeholder?: string | null;
  required: boolean;
  options?: AssistantCollectOption[] | null;
}

/** One reviewed line in the assembled draft. */
export interface AssistantDraftItem {
  key: string;
  label: string;
  value: string;
}

export interface AssistantCollectRequest {
  requestType: string;
  /** The running draft; advisory — the server re-validates every entry. */
  collected: Record<string, string>;
  /** Optional field keys the user explicitly skipped; advisory and server-sanitized. */
  skippedFields?: string[];
  /** The user's latest answer for the currently asked field. Omit on start. */
  message?: string;
  conversationId?: string;
}

export interface AssistantCollectResponse {
  attachments?: import('./requests').AttachmentsConfig;
  reviewFields?: AssistantCollectField[];
  /** collecting | review | handoff */
  status: string;
  requestType: string;
  displayName: string;
  reply: string;
  /** The canonical draft the server trusts — replace the local copy with this each turn. */
  collected: Record<string, string>;
  /** The canonical optional-field skip set — replace the local copy with this each turn. */
  skippedFields: string[];
  nextField?: AssistantCollectField | null;
  draft?: AssistantDraftItem[] | null;
  titlePreview?: string | null;
  /** Prefilled form URL, present only on "handoff". */
  formUrl?: string | null;
  /** Absolute external URL to open, present when a router option forwards outside AskIAM. */
  externalUrl?: string | null;
  note?: string | null;
}

export function sendAssistantCollectTurn(request: AssistantCollectRequest): Promise<AssistantCollectResponse> {
  return postJson<AssistantCollectResponse>('/api/assistant/collect/turn', request);
}
